"use client";

/**
 * Upload Page
 *
 * File import with drag-drop, URL import tab, file list with cost,
 * folder selection, and submit button.
 *
 * Route: /upload
 */

import { Suspense, useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { track, trackPageview, trackFunnel, trackFailure, trackSentryRevenue } from "@/lib/integrations/sentry-tracking";
import { sanitizeString } from "@speakai/common/src/utils/tracking";
import { trackUploadBegin, trackBeginCheckout, trackUploadPurchase, trackUploadAbandon, getIsMobile, pushDataLayer } from "@/lib/integrations/ga4";
import { Button } from "@speakai/ui/button";
import { Input } from "@speakai/ui/input";
import { Card } from "@speakai/ui/card";
import { useToast } from "@speakai/ui/toast";
import { PageContentSkeleton } from "@/components/shared/page-content-skeleton";
import { Link2, FolderOpen, LoaderCircle, CircleCheckBig, Info, Lock, MessageSquare, AlertCircle, CreditCard, Wallet, FileText, Languages, Mic, type LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { Img } from "@/components/shared/img";
import { useTranslation, Trans } from "react-i18next";
import { useUpload } from "@/features/upload/hooks/use-upload";
import { useSlowProcessing } from "@/features/upload/hooks/use-slow-processing";
import { useAppStore } from "@/stores";
import { usePaywallAtIntent } from "@/features/subscription/hooks/use-paywall-at-intent";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import { dashboardKeys, folderKeys, mediaKeys, paymentKeys, subscriptionKeys } from "@/queries/keys";
import { FileImport } from "@/features/upload/components/file-import";
import { FileList } from "@/features/upload/components/file-list";
import { PhoneAppNote } from "@/features/upload/components/phone-app-note";
import { UploadStepper, type UploadStep } from "@/features/upload/components/upload-stepper";
import { detectPlatform } from "@/features/upload/url-validators";
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@speakai/ui/dialog";
import { formatDuration } from "@/lib/utils/format";
import { TRANSLATION_LANGUAGES } from "@/lib/utils/language";
import { useTranscriptionLanguages } from "@/features/profile/queries";
import { cn } from "@/lib/utils/cn";
import { SelectSource } from "@/features/subscription/components/select-source";
import { AddSource } from "@/features/subscription/components/add-source";
import { AddBalance } from "@/features/subscription/components/add-balance";
import { useCreateChargeByBalance, useCreatePaymentIntent, useCreateChargeByPaddle } from "@/features/subscription/mutations";
import { usePaymentMethods } from "@/features/subscription/payment-queries";
import { confirmStripePayment } from "@/features/subscription/payment/stripe";
import { pollBalanceAfterPaddleCharge } from "@/features/subscription/payment/paddle";
import { PaymentSourceType, ADD_BALANCE_MIN_USD, ADD_BALANCE_DEFAULT_USD, STRIPE_CARD_MINIMUM_USD, TransactionType, TransactionSourceType, TransactionSourceName, SubscriptionStatus } from "@/features/subscription/types";
import { usePersonalizationContext } from "@/hooks/use-personalization-context";
import { usePermissions } from "@/hooks/use-permissions";
import { Tooltip } from "@speakai/ui/tooltip";
import {
  calculateTranslationCost,
  calculateBatchTranslationCost,
  calculateDubbingCost,
} from "@/features/upload/cost-calculator";
import {
  useCreditEstimate,
  type CreditEstimateItem,
} from "@/features/upload/hooks/use-credit-estimate";
import { deriveCredits, formatCredits } from "@/features/subscription/hooks/use-credits";
import { getGoalPromptSeed } from "@/lib/onboarding/goal-prompt-seeds";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useAuth } from "@/hooks/use-auth";
import { bookingCalendarUrl, withBookingUtms } from "@/lib/utils/calendly";
import {
  resolveDefaultUploadFolderId,
  getLastUploadFolderId,
  setLastUploadFolderId,
} from "@/features/upload/default-folder";

interface Folder {
  folderId: string;
  name: string;
}

const buildBalanceChargePayload = (totalAmount: number) => ({
  totalAmount,
  sourceId: "balance",
  sourceType: TransactionSourceType.MediaType,
  sourceName: TransactionSourceName.Upload,
  transactionType: TransactionType.Balance,
  description: "MEDIA_CHARGE",
});

export interface UploadPageContentProps {
  mode?: "upload" | "translate";
  onComplete?: () => void;
  isDialog?: boolean;
  onNavigate?: (target: "folder" | "file" | "dashboard", id?: string, folderId?: string) => void;
  seedFolderId?: string;
  onUploadComplete?: (mediaId: string, folderId: string) => void;
}

export function UploadPageContent({
  mode: modeProp,
  onComplete,
  isDialog = false,
  onNavigate,
  seedFolderId,
  onUploadComplete,
}: UploadPageContentProps = {}) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const initialFolderId = searchParams.get("folderId") || "";
  const modeFromUrl = searchParams.get("mode") === "translate" ? "translate" : "upload";
  const mode = modeProp ?? modeFromUrl;
  const isTranslateMode = mode === "translate";
  // translate mode keeps "translation" for parity with shipped tracking events
  const funnelName: "upload" | "translation" = isTranslateMode ? "translation" : "upload";
  const ga4Mode: "upload" | "translate" = isTranslateMode ? "translate" : "upload";

  const pendingImportUrl = useAppStore((s) => s.pendingImportUrl);
  const setPendingImportUrl = useAppStore((s) => s.setPendingImportUrl);
  const openSubscriptionGate = useAppStore((s) => s.openSubscriptionGate);
  const { keepGoing } = usePaywallAtIntent();

  const { user } = useAuth();
  const { t } = useTranslation();
  const defaultTargetLanguage = useMemo(
    () => (isTranslateMode ? TRANSLATION_LANGUAGES[0]?.transcriptionCode ?? "" : ""),
    [isTranslateMode]
  );
  // Source (transcription) options come from the same live list as Profile → Preferences
  // (`useTranscriptionLanguages`), so the upload source dropdown matches it exactly.
  const { data: transcriptionLanguages = [] } = useTranscriptionLanguages();
  const sourceLanguageOptions = useMemo(
    () =>
      transcriptionLanguages
        .map((l) => ({ code: l.code, label: l.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [transcriptionLanguages]
  );
  // Target (translation/dubbing) options stay on TRANSLATION_LANGUAGES — the set we can translate into.
  const targetLanguageOptions = useMemo(
    () => TRANSLATION_LANGUAGES.map((l) => ({ code: l.transcriptionCode, label: l.language })),
    []
  );
  const personalization = usePersonalizationContext();
  const upload = useUpload({
    mode,
    defaultTargetLanguage,
    goal: personalization.onboardingGoal,
    enableFunnelTracking: true,
  });
  const hasMissingTarget = useMemo(() => {
    if (!isTranslateMode) return false;
    return [...upload.files, ...upload.urlItems].some((row) => !row.targetLanguage);
  }, [isTranslateMode, upload.files, upload.urlItems]);

  useEffect(() => {
    if (isDialog) return;
    if (isTranslateMode) {
      // TranslatePage owns the translate-mode pageview + funnel_start; we still
      // fire the begin signal here for funnel parity.
      trackUploadBegin("translate");
      return;
    }
    trackPageview("upload");
    trackFunnel(funnelName, "funnel_start");
    trackUploadBegin("upload");
  }, [isDialog, isTranslateMode]);

  useEffect(() => {
    if (upload.pricingSummary.isExpired) {
      trackFunnel("paywall", "view", { trigger: "upload_expired" });
    }
  }, [upload.pricingSummary.isExpired]);

  const [urlInput, setUrlInput] = useState("");
  const hasTriggeredImportRef = useRef("");
  const [paymentStepOpen, setPaymentStepOpen] = useState(false);
  const [selectedSource, setSelectedSource] = useState<{ id: string; type: "card" | "paddle" | "balance" } | null>(null);
  const [addSourceOpen, setAddSourceOpen] = useState(false);
  // True while the Paddle webhook reconciliation poll is running after a charge —
  // surfaces the "We're confirming your PayPal payment" UX bridge.
  const [paddleReconciling, setPaddleReconciling] = useState(false);
  // True while a just-connected PayPal method is being confirmed active. Charging
  // before the Paddle subscription is active fails server-side; gate Pay Now on this.
  const [paddleActivating, setPaddleActivating] = useState(false);

  const [addBalanceOpen, setAddBalanceOpen] = useState(false);
  const [addBalanceShortfall, setAddBalanceShortfall] = useState<number | undefined>();
  const [addBalanceContext, setAddBalanceContext] = useState<"pre-s3-shortfall" | "step2-card-min" | "step2-paddle-min" | null>(null);

  // Tracks whether a charge succeeded this session — gates the all-files-failed
  // refund path so it only renders when money was actually captured.
  const [chargeWasMade, setChargeWasMade] = useState(false);

  const [entireBalanceDialogOpen, setEntireBalanceDialogOpen] = useState(false);

  const step2HeaderRef = useRef<HTMLHeadingElement>(null);
  // Receives focus when Step 2 dismisses (cancel / go back) so keyboard focus
  // doesn't drop to <body>.
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  // Timestamp of the last "target language required" toast. Suppresses identical
  // toasts fired within a 2-second window so rapid Submit clicks don't stack.
  const lastTargetToastAtRef = useRef<number>(0);

  const toastApi = useToast();
  const createChargeByBalance = useCreateChargeByBalance();
  const createPaymentIntent = useCreatePaymentIntent();
  const createChargeByPaddle = useCreateChargeByPaddle();
  const { data: paddleSources = [], refetch: refetchPaddleSources } = usePaymentMethods(PaymentSourceType.Paddle);
  const isCharging = createChargeByBalance.isPending || createPaymentIntent.isPending || createChargeByPaddle.isPending;

  const { canManageBilling } = usePermissions();
  const { data: savedMethods } = usePaymentMethods();
  const hasCardOnFile = (savedMethods ?? []).length > 0;
  const defaultCard = useMemo(
    () => (savedMethods ?? []).find((m) => m.isDefault) ?? savedMethods?.[0] ?? null,
    [savedMethods],
  );
  useEffect(() => {
    if (upload.status === "topup_required" && !selectedSource && defaultCard) {
      setSelectedSource({ id: defaultCard.id, type: "card" });
    }
  }, [upload.status, defaultCard, selectedSource]);
  const queryClient = useQueryClient();

  // Refresh subscription (credits/grant) and wallet balance when the upload flow
  // opens, so the cost gate never runs on stale cache (subscription staleTime is
  // 5m, balance 30s). Mirrors the Angular upload ngOnInit refetch.
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: subscriptionKeys.current() });
    queryClient.invalidateQueries({ queryKey: paymentKeys.balance() });
  }, [queryClient]);

  useEffect(() => {
    if (initialFolderId) {
      upload.setFolderId(initialFolderId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFolderId]);

  useEffect(() => {
    if (isDialog && seedFolderId && !upload.folderId) upload.setFolderId(seedFolderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fills the URL input from the importUrl query param (page mode) or
  // pendingImportUrl store (dialog mode). Does NOT auto-submit — user must
  // click Import to avoid folder-timing bugs and re-trigger on refresh.
  useEffect(() => {
    const url = isDialog ? pendingImportUrl : searchParams.get("importUrl");
    if (!url) return;
    if (hasTriggeredImportRef.current === url) return;
    hasTriggeredImportRef.current = url;
    setUrlInput(url);
    if (isDialog) {
      setPendingImportUrl(null);
    } else {
      const next = new URLSearchParams(searchParams.toString());
      next.delete("importUrl");
      const qs = next.toString();
      navigate(qs ? `${pathname}?${qs}` : pathname, { replace: true });
    }
  }, [isDialog, pendingImportUrl, setPendingImportUrl, searchParams, pathname, navigate]);

  // Consumes pendingUploadFile from store (written by dashboard drag-drop).
  // Gated on upload.folderId so addFiles snapshots a hydrated folderId, and
  // hasConsumedPendingRef guards against StrictMode double-invoke in dev.
  // Stale files persist in Zustand until next /upload visit if the user
  // navigates away mid-redirect — accepted trade-off vs adding TTL infra.
  const pendingUploadFile = useAppStore((s) => s.pendingUploadFile);
  const setPendingUploadFile = useAppStore((s) => s.setPendingUploadFile);
  const hasConsumedPendingRef = useRef(false);
  useEffect(() => {
    if (pendingUploadFile && upload.folderId && !hasConsumedPendingRef.current) {
      hasConsumedPendingRef.current = true;
      upload.addFiles([pendingUploadFile]);
      setPendingUploadFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingUploadFile, upload.folderId]);

  const { data: folders = [] } = useQuery({
    queryKey: folderKeys.lists(),
    queryFn: async () => {
      const res = await apiClient.get<{ folders: Folder[]; totalCount: number }>("/folder");
      if (res.success && res.data) return res.data.folders ?? [];
      return [];
    },
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (initialFolderId || seedFolderId) return;
    if (folders.length > 0 && !upload.folderId) {
      const id = resolveDefaultUploadFolderId(folders, getLastUploadFolderId(user?._id));
      if (id) upload.setFolderId(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folders]);

  const handleUrlSubmit = async () => {
    if (!urlInput.trim()) return;
    await upload.importFromUrl(urlInput.trim());
    setUrlInput("");
  };

  const totalDuration =
    upload.files.reduce((sum, f) => sum + f.duration, 0) +
    upload.urlItems.reduce((sum, u) => sum + u.duration, 0);

  // Translation cost — aggregate post-quota, after free-trial first-translation quota is applied.
  // Per-file payload (use-upload.ts) stores GROSS values; this dollar total is
  // post-quota for what we charge the user. Server reconciles via totalAmount.
  // URL imports contribute the same way as uploaded files (audio/video only).
  const totalChars = isTranslateMode
    ? upload.files
      .filter((f) => f.mediaCategory !== "text")
      .reduce((sum, f) => sum + calculateTranslationCost(f.duration).characters, 0) +
    upload.urlItems.reduce(
      (sum, u) => sum + calculateTranslationCost(u.duration).characters,
      0,
    )
    : 0;

  // Per-row enableDubbing × duration × $1/min.
  const dubbingCost = isTranslateMode
    ? upload.files.reduce(
      (sum, f) => sum + (f.enableDubbing ? calculateDubbingCost(f.duration) : 0),
      0
    ) +
    upload.urlItems.reduce(
      (sum, u) => sum + (u.enableDubbing ? calculateDubbingCost(u.duration) : 0),
      0,
    )
    : 0;

  const translationCost = isTranslateMode
    ? calculateBatchTranslationCost(totalChars, upload.pricingSummary.freeCharacters ?? 0)
    : 0;

  const effectiveTotalAmount = parseFloat(
    (upload.pricingSummary.displayAmount + translationCost + dubbingCost).toFixed(2)
  );

  const { creditMode } = deriveCredits(upload.subscription);
  const estimateItems = useMemo<CreditEstimateItem[]>(
    () =>
      creditMode
        ? [
            ...upload.files.map((f) => ({
              durationSeconds: f.duration,
              mimeType: f.mediaType,
              sourceLanguage: f.sourceLanguage,
              sizeBytes: f.file?.size,
              mediaCategory: f.mediaCategory,
              isTranslation: isTranslateMode,
              enableDubbing: f.enableDubbing,
              characters: f.mediaCategory === "text" ? f.characters : undefined,
            })),
            ...upload.urlItems.map((u) => ({
              durationSeconds: u.duration,
              mimeType: u.mediaType,
              sourceLanguage: u.sourceLanguage,
              sizeBytes: u.size,
              mediaCategory: u.mediaCategory,
              isTranslation: isTranslateMode,
              enableDubbing: u.enableDubbing,
              // Flagged so the quote matches the charge; the server prices it and stays the authority.
              isYoutubeImport: detectPlatform(u.originalUrl)?.icon === "youtube",
            })),
          ]
        : [],
    [creditMode, upload.files, upload.urlItems, isTranslateMode]
  );
  const { data: creditEstimate } = useCreditEstimate(estimateItems, creditMode);
  // Map each file/url id to its per-item credit cost. estimateItems (and thus
  // creditEstimate.items) are positional: files first, then urlItems — so the
  // media's amount reflects its real credit charge instead of the legacy $0.
  const creditAmountByFileId = useMemo<Record<string, number>>(() => {
    if (!creditMode || !creditEstimate?.items) return {};
    const map: Record<string, number> = {};
    upload.files.forEach((f, i) => { map[f.id] = creditEstimate.items[i]?.credits ?? 0; });
    upload.urlItems.forEach((u, i) => { map[u.id] = creditEstimate.items[upload.files.length + i]?.credits ?? 0; });
    return map;
  }, [creditMode, creditEstimate, upload.files, upload.urlItems]);

  // User pays only the cost their existing credits (grant + wallet) don't cover.
  const grossCredits = creditEstimate?.credits ?? 0;
  const creditsRemainingNow = upload.subscription?.creditsRemaining ?? 0;
  const creditsApplied = creditEstimate ? Math.min(grossCredits, creditsRemainingNow) : 0;
  const netDueNow = creditEstimate ? Math.max(0, grossCredits - creditsRemainingNow) : 0;

  // Free-quota cost-row affordance: translation covered by quota when there are gross chars
  // but the post-quota billable evaluates to $0 — surface the affordance.
  const isFreeCovered = isTranslateMode && totalChars > 0 && translationCost === 0;

  // Dubbing cohort gate — disable dubbing toggle for free-trial users with $0 balance.
  // Prevents free-trial users self-inflicting $1/min × N files sticker shock.
  // Paying users (any balance) and non-trial users see no change.
  const dubbingGateBlocked =
    isTranslateMode && upload.pricingSummary.isFreePlan === true && upload.currentBalance === 0;

  // Per-file translate row errors (set at submit time via validateBatch).
  // Shown inline under each file's target Combobox; submit blocked until cleared.
  const [perFileTranslateErrors, setPerFileTranslateErrors] = useState<Record<string, string>>({});

  // Cascade-fired flag — fires once per batch session. Reset when the queue clears.
  // useRef intentional: mutations are synchronous and must not trigger re-renders.
  const cascadeFiredRef = useRef(false);
  useEffect(() => {
    if (upload.files.length === 0 && upload.urlItems.length === 0) cascadeFiredRef.current = false;
  }, [upload.files.length, upload.urlItems.length]);

  useEffect(() => {
    if (!isTranslateMode || cascadeFiredRef.current) return;
    if (upload.files.length < 2 || !defaultTargetLanguage) return;
    for (const f of upload.files) {
      if (!f.targetLanguage) upload.updateFile(f.id, { targetLanguage: defaultTargetLanguage });
    }
    cascadeFiredRef.current = true;
  }, [isTranslateMode, upload.files, defaultTargetLanguage, upload.updateFile]);

  const isDeductFromBalance = upload.pricingSummary.isDeductFromBalance;
  const isExactBalanceMatch =
    isDeductFromBalance &&
    Math.abs(upload.currentBalance - effectiveTotalAmount) < 0.005 &&
    effectiveTotalAmount > 0;

  const handleConfirmUploadRef = useRef<() => Promise<void>>(async () => { });
  const resumeUploadAfterPayment = useCallback(() => {
    creditBypassRef.current = true;
    isSubmittingRef.current = true;
    handleConfirmUploadRef.current().catch((err) => {
      isSubmittingRef.current = false;
      creditBypassRef.current = false;
      toastApi.error(t("UPLOAD_FILE.UPLOAD_ERROR", "Upload failed"), err instanceof Error ? err.message : "");
    });
  }, [toastApi, t]);
  const handleConfirmUpload = useCallback(async () => {
    const { isExpired, isDeductFromBalance, hasFreeMinutes, hasFreeTextNotes } = upload.pricingSummary;
    // On resume from the expired-subscription gate, re-enter via the ref so we
    // pick up the LATEST handleConfirmUpload with a fresh pricingSummary —
    // calling the bare function would capture a stale closure and loop.
    if (isExpired) {
      // Dialog's useEffect is the single source of paywall.view events; no duplicate fire here.
      openSubscriptionGate({
        source: "upload_expired",
        onResume: () => {
          handleConfirmUploadRef.current().catch((err) => {
            const msg = err instanceof Error ? err.message : t("UPLOAD_FILE.UPLOAD_ERROR", "Upload failed");
            toastApi.error(t("UPLOAD_FILE.UPLOAD_ERROR", "Upload failed"), msg);
          });
        },
      });
      isSubmittingRef.current = false;
      return;
    }
    // Direct-deposit workspaces never self-serve a card add — route to the
    // contact-sales UI instead. Must run after the expired-trial check above.
    if (personalization.paymentSource === PaymentSourceType.Direct_Deposit) {
      upload.enterDirectDepositGate();
      setPaymentStepOpen(true);
      isSubmittingRef.current = false;
      return;
    }
    if (creditMode && !creditBypassRef.current) {
      const remainingCredits = upload.subscription?.creditsRemaining ?? 0;
      const neededCredits = creditEstimate?.credits ?? 0;
      if (neededCredits > 0 && neededCredits > remainingCredits) {
        const shortfall = Math.max(Math.ceil(neededCredits - remainingCredits), ADD_BALANCE_MIN_USD);
        isSubmittingRef.current = false;
        if (keepGoing) {
          openSubscriptionGate({
            source: "upload_balance_zero",
            topUpAmount: shortfall,
            onResume: resumeUploadAfterPayment,
          });
          return;
        }
        setAddBalanceContext("pre-s3-shortfall");
        setAddBalanceShortfall(shortfall);
        setAddBalanceOpen(true);
        return;
      }
    }
    creditBypassRef.current = false;
    // S3 upload always runs before any payment gate so the user keeps their
    // files even if the charge fails downstream.
    const processed = await upload.uploadFilesOnly(creditMode ? creditAmountByFileId : undefined);
    if (processed.length === 0) { isSubmittingRef.current = false; return; }
    if (creditMode) {
      track("create", "credit_upload");
      // Server records only the grant slice; charge the paid overflow to the wallet.
      const overflow = Math.max(0, (creditEstimate?.credits ?? 0) - (upload.subscription?.creditsGrantRemaining ?? 0));
      if (overflow > 0) {
        try {
          const chargeRes = await createChargeByBalance.mutateAsync(buildBalanceChargePayload(overflow));
          if (!chargeRes.success) throw new Error(chargeRes.error ?? "charge_failed");
          trackSentryRevenue("upload.charged", overflow, { payment_method: "balance", file_count: totalFiles });
        } catch (err) {
          const msg = err instanceof Error ? err.message : t("PAYMENT.PAYMENT_FAILED", "Payment failed");
          toastApi.error(t("UPLOAD_FILE.UPLOAD_ERROR", "Upload failed"), msg);
          isSubmittingRef.current = false;
          return;
        }
      }
      await upload.chargeAndProcess(processed);
      queryClient.invalidateQueries({ queryKey: mediaKeys.all });
      queryClient.invalidateQueries({ queryKey: folderKeys.all });
      // The overflow charge moved the wallet: refresh the surfaces that render it (topbar pill,
      // usage widgets, low-credit state) instead of waiting out staleTime.
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.current() });
      queryClient.invalidateQueries({ queryKey: dashboardKeys.usage() });
      isSubmittingRef.current = false;
      return;
    }
    if (
      effectiveTotalAmount === 0 ||
      hasFreeMinutes ||
      hasFreeTextNotes ||
      (isTranslateMode &&
        upload.pricingSummary.hasFreeCharacters &&
        totalChars <= (upload.pricingSummary.freeCharacters ?? 0))
    ) {
      track("create", "free_upload");
      await upload.chargeAndProcess(processed);
      queryClient.invalidateQueries({ queryKey: mediaKeys.all });
      queryClient.invalidateQueries({ queryKey: folderKeys.all });
      isSubmittingRef.current = false;
      return;
    }
    // Balance covers the cost → silent balance charge, no Step 2.
    if (isDeductFromBalance && upload.currentBalance >= effectiveTotalAmount) {
      // apiClient.post resolves {success:false} on 4xx/5xx instead of throwing,
      // so .success MUST be checked — otherwise a failed charge is treated as
      // success and the user gets free media.
      try {
        const chargeRes = await createChargeByBalance.mutateAsync(buildBalanceChargePayload(effectiveTotalAmount));
        if (!chargeRes.success) throw new Error(chargeRes.error ?? "charge_failed");
        trackSentryRevenue("upload.charged", effectiveTotalAmount, { payment_method: "balance", file_count: totalFiles });
        trackUploadPurchase(effectiveTotalAmount, totalFiles, "balance", ga4Mode);
      } catch (err) {
        const msg = err instanceof Error ? err.message : t("PAYMENT.PAYMENT_FAILED", "Payment failed");
        toastApi.error(t("UPLOAD_FILE.UPLOAD_ERROR", "Upload failed"), msg);
        trackFailure("payment", "fail", { error_reason: msg, charge_type: "media_upload_balance_auto", payment_method: "balance" });
        isSubmittingRef.current = false;
        return;
      }
      // chargeAndProcess throws on !processRes.success — distinct toast so the
      // user knows they were charged even when processing fails afterwards.
      try {
        await upload.chargeAndProcess(processed);
        queryClient.invalidateQueries({ queryKey: mediaKeys.all });
        queryClient.invalidateQueries({ queryKey: folderKeys.all });
        isSubmittingRef.current = false;
      } catch (processErr) {
        const msg = processErr instanceof Error ? processErr.message : "";
        toastApi.error(t("UPLOAD_FILE.PAYMENT_OK_PROCESS_FAILED", "Payment succeeded but processing failed"), msg);
        trackFailure("payment", "process_fail_after_charge", { error_reason: msg, charge_type: "media_upload_balance_auto", payment_method: "balance" });
        isSubmittingRef.current = false;
      }
      return;
    }
    // Uncovered → inline Step 2. Driving topup_required is required: inPaymentStep
    // gates Step 2 rendering, so paymentStepOpen alone leaves a blank card.
    trackFunnel("paywall", "view", { trigger: "upload_over_limit" });
    upload.enterTopupRequired();
    setPaymentStepOpen(true);
  }, [upload, creditMode, creditEstimate, effectiveTotalAmount, createChargeByBalance, toastApi, t, openSubscriptionGate, personalization, isTranslateMode, totalChars, keepGoing, resumeUploadAfterPayment]);

  useEffect(() => { handleConfirmUploadRef.current = handleConfirmUpload; }, [handleConfirmUpload]);

  useEffect(() => {
    // Move keyboard focus to the Step 2 header so screen readers announce the
    // transition without requiring the user to tab into it manually.
    if (paymentStepOpen && step2HeaderRef.current) {
      step2HeaderRef.current.focus();
    }
  }, [paymentStepOpen]);

  useEffect(() => {
    if (upload.status === "complete") {
      if (upload.folderId) setLastUploadFolderId(user?._id, upload.folderId);
      queryClient.invalidateQueries({ queryKey: mediaKeys.lists() });
      trackFunnel(funnelName, "complete_viewed", {
        file_count: upload.uploadedMediaIds.length,
        is_paid: effectiveTotalAmount > 0,
      });
      onComplete?.();
      // Chat AV dialog seeds one file; if extra files were added, link the first uploaded media to the chat.
      if (isDialog && upload.uploadedMediaIds.length >= 1 && upload.uploadedMediaIds[0]?.mediaId) {
        onUploadComplete?.(upload.uploadedMediaIds[0].mediaId, upload.folderId);
      }
    }
    // One-way latch: fires once when status → "complete". uploadedMediaIds and
    // effectiveTotalAmount are stable at that moment, so adding them as deps
    // would re-fire on any pricing update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upload.status, queryClient]);

  const isPayingRef = useRef(false);
  const isSubmittingRef = useRef(false);
  const creditBypassRef = useRef(false);
  const pendingSourceRef = useRef<{ id: string; type: "card" | "paddle" | "balance" } | null>(null);
  const handleStep2Pay = useCallback(async () => {
    const source = pendingSourceRef.current ?? selectedSource;
    pendingSourceRef.current = null;
    if (!source || isPayingRef.current) return;
    isPayingRef.current = true;
    // On retry from charge_failed, re-enter charge_confirm so the state machine
    // reflects "ready to pay" while awaiting the call.
    upload.enterChargeConfirm();
    let chargeSucceeded = false;
    try {
      // charge_processing also adds the status to isUploading so the dropzone
      // and file list are disabled during the network round-trip.
      upload.enterChargeProcessing();
      // apiClient.post resolves {success:false} on 4xx/5xx instead of throwing,
      // so .success MUST be checked — otherwise a failed charge is treated as
      // success and the user gets free media.
      if (source.type === "balance") {
        const chargeRes = await createChargeByBalance.mutateAsync(buildBalanceChargePayload(effectiveTotalAmount));
        if (!chargeRes.success) throw new Error(chargeRes.error ?? "charge_failed");
      } else if (source.type === "card") {
        if (effectiveTotalAmount < STRIPE_CARD_MINIMUM_USD) {
          // Pre-charge UX gate; the server still enforces minimums independently.
          // Non-admins get a toast; admins get the AddBalance dialog to top up directly.
          if (!canManageBilling) {
            toastApi.error(
              t("UPLOAD_FILE.CARD_MINIMUM_TITLE", "Card minimum is $0.50"),
              t("UPLOAD_FILE.CONTACT_ADMIN", "Ask your billing admin to top up the workspace credit."),
            );
            upload.enterChargeConfirm();
            isPayingRef.current = false;
            return;
          }
          setAddBalanceContext("step2-card-min");
          setAddBalanceShortfall(Math.max(effectiveTotalAmount, ADD_BALANCE_MIN_USD));
          setAddBalanceOpen(true);
          isPayingRef.current = false;
          return;
        }
        const intent = await createPaymentIntent.mutateAsync({
          totalAmount: effectiveTotalAmount, sourceId: source.id,
          sourceType: TransactionSourceType.MediaType, sourceName: TransactionSourceName.Upload,
          transactionType: TransactionType.Card, description: "MEDIA_CHARGE",
        });
        if (!intent.success || !intent.data?.clientSecret) {
          trackFailure("payment", "fail", { error_reason: intent.error ?? "no_client_secret", charge_type: "media_upload", payment_method: "card" });
          throw new Error(t("PAYMENT.PAYMENT_FAILED", "Payment failed"));
        }
        // confirmStripePayment throws on Stripe error with a sanitized but
        // untranslated message — let it propagate to the outer catch.
        await confirmStripePayment(intent.data.clientSecret, source.id);
      } else {
        if (effectiveTotalAmount < STRIPE_CARD_MINIMUM_USD) {
          if (!canManageBilling) {
            toastApi.error(
              t("UPLOAD_FILE.CARD_MINIMUM_TITLE", "Card minimum is $0.50"),
              t("UPLOAD_FILE.CONTACT_ADMIN", "Ask your billing admin to top up the workspace credit."),
            );
            upload.enterChargeConfirm();
            isPayingRef.current = false;
            return;
          }
          setAddBalanceContext("step2-paddle-min");
          setAddBalanceShortfall(Math.max(effectiveTotalAmount, ADD_BALANCE_MIN_USD));
          setAddBalanceOpen(true);
          isPayingRef.current = false;
          return;
        }
        const paddleRes = await createChargeByPaddle.mutateAsync({ amount: effectiveTotalAmount, description: "MEDIA_CHARGE" });
        if (!paddleRes.success) throw new Error(paddleRes.error ?? "charge_failed");
        const expectedMinBalance = upload.currentBalance + effectiveTotalAmount;
        setPaddleReconciling(true);
        pollBalanceAfterPaddleCharge({ expectedMinBalance })
          .finally(() => setPaddleReconciling(false));
      }
      chargeSucceeded = true;
      setChargeWasMade(true);
      trackSentryRevenue("upload.charged", effectiveTotalAmount, { payment_method: source.type, file_count: totalFiles });
      trackUploadPurchase(effectiveTotalAmount, totalFiles, source.type, ga4Mode);
      // Inner try so processing failure surfaces a distinct toast — the user
      // needs to know their money was captured even if processing fails.
      try {
        await upload.chargeAndProcess(upload.processedFiles);
        queryClient.invalidateQueries({ queryKey: mediaKeys.all });
        queryClient.invalidateQueries({ queryKey: folderKeys.all });
      } catch (processErr) {
        const pmsg = processErr instanceof Error ? processErr.message : "";
        toastApi.error(t("UPLOAD_FILE.PAYMENT_OK_PROCESS_FAILED", "Payment succeeded but processing failed"), pmsg);
        trackFailure("payment", "process_fail_after_charge", { error_reason: pmsg, charge_type: "media_upload", payment_method: source.type });
      }
    } catch (err) {
      if (chargeSucceeded) return; // inner catch already toasted the processing failure
      const msg = err instanceof Error ? err.message : t("PAYMENT.PAYMENT_FAILED", "Payment failed");
      toastApi.error(t("UPLOAD_FILE.UPLOAD_ERROR", "Upload failed"), msg);
      trackFailure("payment", "fail", { error_reason: msg, charge_type: "media_upload", payment_method: source.type });
      // Keep Step 2 visible so the user can retry or cancel without a blank card.
      upload.enterChargeFailed();
    } finally {
      isPayingRef.current = false;
      isSubmittingRef.current = false;
      // Always dismiss Step 2 once charge succeeded — re-opening it would risk
      // a double-charge on retry.
      if (chargeSucceeded) setPaymentStepOpen(false);
    }
  }, [selectedSource, effectiveTotalAmount, createChargeByBalance, createPaymentIntent, createChargeByPaddle, upload, toastApi, t, setChargeWasMade]);

  const isUploading =
    upload.status === "uploading" ||
    upload.status === "processing" ||
    upload.status === "awaiting_payment" ||
    upload.status === "charge_processing";
  // "processing" only: it is reached after payment settles, so leaving the page is safe there.
  // During "uploading" the charge has not happened and leaving would strand the upload.
  const isBackgroundProcessing = useSlowProcessing(upload.status === "processing");
  // Status-machine gate for Step 2 — must align with paymentStepOpen (the UI gate)
  // to avoid a blank card when paymentStepOpen flips true with no matching status.
  const inPaymentStep =
    upload.status === "awaiting_payment" ||
    upload.status === "topup_required" ||
    upload.status === "add_payment_method" ||
    upload.status === "charge_confirm" ||
    upload.status === "charge_processing" ||
    upload.status === "charge_failed" ||
    upload.status === "direct_deposit_gate";
  const isComplete = upload.status === "complete";
  const hasFiles = upload.files.length > 0 || upload.urlItems.length > 0;
  const totalFiles = upload.files.length + upload.urlItems.length;

  // Triggers the refund-path banner: charge was captured but every file failed
  // to upload, so the user needs a way to recover their balance.
  const allFilesFailed =
    chargeWasMade &&
    upload.files.length > 0 &&
    upload.files.every((f) => !f.isValid) &&
    upload.processedFiles.length === 0 &&
    !isComplete;

  async function handleRetryAll() {
    for (const f of upload.files) {
      if (!f.isValid) await upload.retryFile(f.id);
    }
  }

  // Fail-closed: any null/uncertain signal falls through to generic copy.
  // planIsLegacy === false narrows away cancelled paid users whose paymentSource
  // is null during rail-migration edge cases.
  const isTrialExpiredRecovery =
    personalization.subscriptionStatus === SubscriptionStatus.Cancelled &&
    personalization.planIsLegacy === false &&
    personalization.paymentSource === null;
  let step2Subhead = t("UPLOAD.STEP2.PAY_TO_CONTINUE", "Pay to continue.");
  if (!personalization.isLoading) {
    if (!hasCardOnFile && upload.currentBalance === 0) {
      if (personalization.isTrialNearExpiry) {
        step2Subhead = t("UPLOAD.STEP2.TRIAL_NEAR_EXPIRY", "Add a payment method to process your files before your trial ends.");
      } else if (isTrialExpiredRecovery) {
        step2Subhead = t("UPLOAD.STEP2.TRIAL_RECOVERY", "Your trial files are saved. Add a payment method to continue processing them.");
      } else {
        step2Subhead = t("UPLOAD.STEP2.PAY_PER_FILE", "Add a payment method. You'll only be charged for files you process.");
      }
    } else if (upload.currentBalance < effectiveTotalAmount) {
      step2Subhead = t("UPLOAD.STEP2.BALANCE_LOW", "Your credit is low. Choose a payment method below.");
    } else {
      step2Subhead = t("UPLOAD.STEP2.CHOOSE_METHOD", "Choose a payment method below.");
    }
  }

  // Completion-screen cohort variants — priority-chained with a fail-closed default.
  let completionHeadline = t("UPLOAD.COMPLETE.HEADLINE_DEFAULT", "Upload complete. Your file is ready to explore.");
  let completionBody = t("UPLOAD.COMPLETE.BODY_DEFAULT", "Open your file to transcribe, analyze, and start with AI Chat.");
  let primaryCtaLabel = t("UPLOAD.COMPLETE.START_CHAT", "Start AI Chat");
  let PrimaryCtaIcon: LucideIcon = MessageSquare;
  let primaryCtaAction: "file" | "folder" | "translation" = "file";
  let completionGoalPrompt: string | null = null;
  let showRecoveryReactivation = false;

  if (isComplete) {
    const singleFile = upload.uploadedMediaIds.length === 1 ? upload.uploadedMediaIds[0] : null;
    const batchCount = upload.uploadedMediaIds.length;
    // hasActivatedUpload === null means "server omitted totalMedia" per the hook
    // contract — falls through to the default branch, not treated as a new user.
    // isNewUser carries the explicit signal.
    const isTrialNew =
      !personalization.isLoading &&
      (personalization.hasActivatedUpload === false || personalization.isNewUser === true);

    if (isTranslateMode) {
      completionHeadline = t("TRANSLATE.COMPLETE_HEADLINE", "Your translation is being created.");
      completionBody = t("TRANSLATE.COMPLETE_BODY", "Your file is translating. We'll email you when it's done.");
      primaryCtaLabel = t("TRANSLATE.OPEN_TRANSLATION", "Open translation");
      PrimaryCtaIcon = Languages;
      primaryCtaAction = "translation";
    } else if (!personalization.isLoading && !canManageBilling) {
      // Team member: collaboration lens, no billing chrome.
      completionHeadline = batchCount >= 2
        ? t("UPLOAD.COMPLETE.TEAM_BATCH_HEADLINE", "{{count}} files ready for your team.", { count: batchCount })
        : t("UPLOAD.COMPLETE.TEAM_SINGLE_HEADLINE", "Ready for your team.");
      completionBody = t("UPLOAD.COMPLETE.TEAM_BODY", "Review in the folder, or open any file to start with AI Chat.");
      primaryCtaLabel = batchCount >= 2
        ? t("UPLOAD.COMPLETE.VIEW_BATCH_CTA", "View batch in folder")
        : t("UPLOAD.COMPLETE.OPEN_FOLDER_CTA", "Open in folder");
      PrimaryCtaIcon = FolderOpen;
      primaryCtaAction = "folder";
    } else if (!personalization.isLoading && isTrialExpiredRecovery) {
      completionHeadline = t("UPLOAD.COMPLETE.RECOVERY_HEADLINE", "You're back. Your file is in.");
      completionBody = t("UPLOAD.COMPLETE.RECOVERY_BODY", "Pick up where you left off.");
      primaryCtaLabel = t("UPLOAD.COMPLETE.OPEN_TRANSCRIPT", "Open your transcript");
      PrimaryCtaIcon = MessageSquare;
      primaryCtaAction = "file";
      showRecoveryReactivation = true;
    } else if (isTrialNew) {
      completionHeadline = batchCount >= 2
        ? t("UPLOAD.COMPLETE.NEW_BATCH_HEADLINE", "{{count}} files ready.", { count: batchCount })
        : t("UPLOAD.COMPLETE.NEW_SINGLE_HEADLINE", "Your file is ready.");
      completionBody = t("UPLOAD.COMPLETE.NEW_BODY", "Open your transcript and start exploring with AI Chat.");
      primaryCtaLabel = t("UPLOAD.COMPLETE.OPEN_TRANSCRIPT", "Open your transcript");
      PrimaryCtaIcon = MessageSquare;
      primaryCtaAction = "file";
      completionGoalPrompt = getGoalPromptSeed(personalization.onboardingGoal);
    } else {
      if (batchCount >= 2) {
        completionHeadline = t("UPLOAD.COMPLETE.NEW_BATCH_HEADLINE", "{{count}} files ready.", { count: batchCount });
      }
      primaryCtaLabel = singleFile
        ? t("UPLOAD.COMPLETE.START_CHAT_ON_FILE", "Start AI Chat on {{name}}", { name: singleFile.name.slice(0, 20) })
        : t("UPLOAD.COMPLETE.START_CHAT", "Start AI Chat");
      PrimaryCtaIcon = MessageSquare;
      primaryCtaAction = "file";
    }
  }

  const stepperSteps: UploadStep[] = (() => {
    const isChargeProcessing = upload.status === "charge_processing";
    const isChargeFailed = upload.status === "charge_failed";
    const isServerProcessing = upload.status === "uploading" || upload.status === "processing";
    const paymentReached = inPaymentStep || isServerProcessing || isComplete;
    const paymentCleared = isServerProcessing || isComplete;

    const uploadStatus: UploadStep["status"] = paymentReached || hasFiles ? "complete" : "active";

    let paymentStatus: UploadStep["status"];
    if (isComplete || paymentCleared) paymentStatus = "complete";
    else if (isChargeFailed) paymentStatus = "error";
    else if (isChargeProcessing) paymentStatus = "processing";
    else if (inPaymentStep && paymentStepOpen) paymentStatus = "active";
    else paymentStatus = "pending";

    let processingStatus: UploadStep["status"];
    if (isComplete) processingStatus = "complete";
    else if (allFilesFailed) processingStatus = "error";
    else if (isServerProcessing) processingStatus = "processing";
    else processingStatus = "pending";

    return [
      { id: 1, label: t("ACTION.UPLOAD"), status: uploadStatus },
      { id: 2, label: t("UPLOAD.STEPPER.PAYMENT", "Payment"), status: paymentStatus },
      { id: 3, label: t("UPLOAD_FILE.STEP_THREE", "Completed"), status: processingStatus },
    ];
  })();

  return (
    <div>
      <div className={cn("w-full mx-auto", isDialog ? "max-w-6xl" : "max-w-screen-xl")}>

        {!isDialog && (
          <div className="flex items-center justify-between px-6 pt-6 pb-5 border-b border-border">
            <div>
              {isTranslateMode ? (
                <h2 className="text-xl font-semibold text-foreground">
                  {t("TRANSLATE_FILE.TITLE", "Translate to Another Language")}
                </h2>
              ) : (
                <h1 className="text-xl font-semibold text-foreground">
                  {t("UPLOAD_FILE.UPLOAD", "Upload Media")}
                </h1>
              )}
              <p className="mt-0.5 text-sm text-muted-foreground">
                {isTranslateMode ? t("TRANSLATE_FILE.DESC", "Upload audio or video to translate into your target language.") : t("UPLOAD_FILE.SUPPORT_TEXT", "Upload audio, video, or text files for transcription.")}
              </p>
            </div>
          </div>
        )}

        <div className={cn(!isDialog ? "p-6 space-y-6" : "space-y-4 min-h-[480px]")}>

          <UploadStepper steps={stepperSteps} className="pb-4 border-b border-border/60" />

          {isBackgroundProcessing && (
            <Card
              className="flex items-start gap-2 border-primary/30 bg-primary/5 px-3 py-2"
              role="status"
              aria-live="polite"
              data-testid="upload-background-processing-notice"
            >
              <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-sm text-foreground">
                {t(
                  "UPLOAD_FILE.PROCESSING_IN_BACKGROUND",
                  "We're processing your files. You'll be notified as each one becomes ready, so you can leave this page.",
                )}
              </p>
            </Card>
          )}

          {isTranslateMode && !hasFiles && !isComplete && !isDialog && (
            <div
              data-testid="translate-pre-file-hero"
              className="rounded-lg border border-border bg-muted/30 p-4 sm:p-5"
            >
              <h2 className="text-sm font-semibold text-foreground">
                {t("TRANSLATE.HERO_TITLE", "Before you upload")}
              </h2>
              <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                <li>{t("TRANSLATE.HERO_STEP_SOURCE", "We auto-detect the language of your file. Change it if you know it.")}</li>
                <li>{t("TRANSLATE.HERO_STEP_TARGET", "Pick the language you want the transcript in.")}</li>
                <li>{t("TRANSLATE.HERO_STEP_EMAIL", "Translation runs in the background. We'll email you when it's ready.")}</li>
              </ul>
            </div>
          )}

          {isComplete && upload.uploadedMediaIds.length > 0 && (
            <Card className="border-success/30 bg-success/5 p-6 space-y-5" data-testid="upload-complete">

              <div className="flex items-start gap-3">
                <CircleCheckBig className="h-6 w-6 text-success mt-0.5 shrink-0" aria-hidden="true" />
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-semibold text-foreground leading-snug">{completionHeadline}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">{completionBody}</p>
                  {completionGoalPrompt && (
                    <p
                      className="mt-1.5 text-sm text-muted-foreground italic"
                      data-testid="upload-complete-goal-prompt"
                    >
                      {completionGoalPrompt}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                {upload.uploadedMediaIds.map((m) => (
                  <div
                    key={m.mediaId}
                    className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
                      <span className="text-sm text-foreground truncate">{m.name}</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={isDialog ? () => onNavigate?.("file", m.mediaId, upload.folderId) : () => upload.goToFile(m.mediaId)}
                      aria-label={`Open ${m.name}`}
                      data-testid={`upload-complete-file-link-${m.mediaId}`}
                      className="shrink-0"
                    >
                      Open →
                    </Button>
                  </div>
                ))}
              </div>

              <div className="border-t border-border" />

              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
                <Button
                  type="button"
                  onClick={() => {
                    const ctaVariant = primaryCtaAction === "folder" ? "folder" : primaryCtaAction === "translation" ? "translation" : showRecoveryReactivation ? "recovery" : primaryCtaLabel.includes("AI Chat") ? "ai_chat" : "transcript";
                    trackFunnel(funnelName, "cta_clicked", { cta_variant: ctaVariant, file_count: upload.uploadedMediaIds.length });
                    if (primaryCtaAction === "folder") {
                      if (isDialog) onNavigate?.("folder", undefined, upload.folderId);
                      else upload.goToFolder();
                    } else if (primaryCtaAction === "translation" && upload.uploadedMediaIds[0]?.mediaId) {
                      const translationFirstId = upload.uploadedMediaIds[0].mediaId;
                      if (isDialog) onNavigate?.("file", translationFirstId, upload.folderId);
                      else upload.goToFile(translationFirstId);
                    } else {
                      const firstId = upload.uploadedMediaIds[0]?.mediaId;
                      // Defensive: outer JSX guard makes this unreachable, but fall
                      // back to folder nav rather than a silent no-op if it breaks.
                      if (!firstId) {
                        if (isDialog) onNavigate?.("folder", undefined, upload.folderId);
                        else upload.goToFolder();
                        return;
                      }
                      if (isDialog) onNavigate?.("file", firstId, upload.folderId);
                      else upload.goToFile(firstId);
                    }
                  }}
                  variant="primary"
                  size="sm"
                  data-testid="upload-complete-primary-cta"
                  aria-label={
                    primaryCtaAction === "file" &&
                      upload.uploadedMediaIds.length === 1 &&
                      upload.uploadedMediaIds[0].name !== "file"
                      ? `${primaryCtaLabel.replace(/ on .*$/, "")} on ${upload.uploadedMediaIds[0].name}`
                      : undefined
                  }
                >
                  <PrimaryCtaIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                  {primaryCtaLabel}
                </Button>
                {primaryCtaAction !== "folder" && primaryCtaAction !== "translation" && (
                  <Button
                    type="button"
                    onClick={isDialog ? () => onNavigate?.("folder", undefined, upload.folderId) : upload.goToFolder}
                    variant="outline"
                    size="sm"
                  >
                    <FolderOpen className="mr-2 h-4 w-4" aria-hidden="true" />
                    {t("ACTION.GO_TO_FOLDER", "Go to folder")}
                  </Button>
                )}
                <Button
                  type="button"
                  onClick={() => {
                    upload.reset();
                    isSubmittingRef.current = false;
                  }}
                  variant="ghost"
                  size="sm"
                >
                  Upload another
                </Button>
              </div>

              {/* Static assurance — every upload sends a completion email; not a user toggle. */}
              <p className="text-xs text-muted-foreground">
                {t("UPLOAD.NOTIFY_STATIC", "We'll email you when your files are ready.")}
              </p>

              {showRecoveryReactivation && (
                <p className="text-sm text-muted-foreground">
                  Ready to continue?{" "}
                  <Link
                    to="/profile/payment"
                    className="text-primary underline hover:text-foreground transition-colors"
                    data-testid="upload-complete-resume-link"
                  >
                    Get full access back
                  </Link>.
                </p>
              )}

            </Card>
          )}

          {allFilesFailed && (
            <div
              role="alert"
              className="rounded-md border border-destructive/50 bg-destructive/10 p-4"
              data-testid="upload-all-failed-refund"
            >
              <h3 className="text-sm font-semibold text-destructive">
                {t("UPLOAD.ALL_FAILED_HEADING", "Your upload didn't complete")}
              </h3>
              <p className="mt-1 text-sm text-foreground">
                {t("UPLOAD.ALL_FAILED_BODY", "We charged your credit but couldn't process the files. Contact support and we'll refund the unused amount.")}
              </p>
              <div className="mt-3 flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={handleRetryAll}>
                  {t("UPLOAD.RETRY_ALL", "Retry all")}
                </Button>
                <a
                  href="mailto:support@speakai.co?subject=Upload%20refund%20request"
                  className="inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {t("UPLOAD.CONTACT_SUPPORT", "Contact support")}
                </a>
              </div>
            </div>
          )}

          {!isComplete && !paymentStepOpen && (
            <>
              {!creditMode &&
                upload.pricingSummary.freeMinutesContext === "trial" &&
                upload.pricingSummary.availableMinutes > 0 && (() => {
                  const m = upload.pricingSummary.availableMinutes;
                  const display = m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
                  return (
                    <Card
                      className="flex items-center gap-2 border-primary/30 bg-primary/5 px-3 py-2"
                      role="status"
                      data-testid="upload-free-minutes-banner"
                    >
                      <Info className="h-4 w-4 text-primary shrink-0" aria-hidden="true" />
                      <p className="text-sm text-foreground">
                        <Trans
                          i18nKey="UPLOAD.FREE_MINUTES_REMAINING"
                          values={{ display }}
                          defaults="You have <1>{{display}}</1> of free minutes remaining on your trial."
                          components={[<span key="0" />, <strong key="1" />]}
                        />
                      </p>
                    </Card>
                  );
                })()}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
                <FileImport
                  onFilesSelected={upload.addFiles}
                  errorMessage={upload.errorMessage}
                  onClearError={() => upload.setErrorMessage("")}
                  disabled={isUploading}
                  onRejected={(n) => toastApi.error(`${n} file(s) not supported`, "")}
                  enableCheckoutTracking
                  isOnline={upload.isOnline}
                />

                <div
                  id="upload-paste-link-panel"
                  className="rounded-xl border border-border bg-muted/20 p-5 flex flex-col gap-4 h-full"
                  data-testid="upload-paste-link-panel"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-full bg-muted shrink-0">
                      <Link2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {t("UPLOAD_FILE.PASTE_LINK_HEADER", "Paste a link")}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {t(
                          "UPLOAD_FILE.PASTE_LINK_YOUTUBE_DESC",
                          "YouTube imports are back. Paste a link and we handle the rest."
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Input
                      type="url"
                      value={urlInput}
                      onChange={(e) => setUrlInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleUrlSubmit();
                      }}
                      placeholder={t("UPLOAD_FILE.PASTE_LINK", "Paste a URL…")}
                      disabled={isUploading || upload.urlImport.isLoading}
                      data-testid="upload-url-input"
                      className="bg-background border-border"
                    />
                    <Button
                      type="button"
                      onClick={handleUrlSubmit}
                      disabled={
                        !urlInput.trim() ||
                        isUploading ||
                        upload.urlImport.isLoading
                      }
                      size="sm"
                      variant="outline"
                      data-testid="upload-url-import"
                      className="shrink-0"
                    >
                      {upload.urlImport.isLoading ? (
                        <>
                          <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
                          <span className="sr-only">{t("UPLOAD_FILE.IMPORTING", "Importing…")}</span>
                        </>
                      ) : (
                        t("UPLOAD_FILE.IMPORT", "Import")
                      )}
                    </Button>
                  </div>

                  {upload.urlImport.error && (
                    <div className="flex items-start gap-1.5 text-sm text-danger" role="alert">
                      <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
                      <span>
                        {upload.urlImport.error} {t("UPLOAD_FILE.CHECK_URL", "Please check the URL and try again.")}
                      </span>
                    </div>
                  )}

                  {upload.urlImport.platformIcons.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5">
                      {upload.urlImport.platformIcons.map((p) => {
                        const isYoutube = p.icon === "youtube";
                        return (
                          <span
                            key={p.icon}
                            className={cn(
                              "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px]",
                              isYoutube
                                ? "bg-danger/10 font-semibold text-danger ring-1 ring-inset ring-danger/25"
                                : "bg-muted font-medium text-muted-foreground"
                            )}
                            data-testid={`upload-platform-icon-${p.icon}`}
                          >
                            <Img
                              src={`/assets/media/icons/platforms/${p.icon}.svg`}
                              alt=""
                              aria-hidden="true"
                              width={isYoutube ? 14 : 12}
                              height={isYoutube ? 14 : 12}
                              className={cn(
                                isYoutube ? "h-3.5 w-3.5" : "h-3 w-3",
                                p.icon === "x-twitter" && "dark:invert"
                              )}
                              loading="lazy"
                              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                            />
                            {p.name}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>

                <Link
                  to="/upload/record"
                  onClick={() => trackFunnel("upload", "record_cta_clicked")}
                  className="rounded-xl border border-border bg-muted/20 p-5 flex flex-col gap-4 h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring no-underline"
                  data-testid="upload-record-cta-tile"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-full bg-muted shrink-0">
                      <Mic className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {t("UPLOAD_FILE.RECORD_TILE_HEADING", "Record Yourself")}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {t("UPLOAD_FILE.RECORD_TILE_SUB", "Capture audio or video right in your browser.")}
                      </p>
                    </div>
                  </div>
                  <div className="mt-auto">
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-primary">
                      {t("UPLOAD_FILE.RECORD_TILE_CTA", "Start Recording")}
                    </span>
                  </div>
                </Link>
              </div>

              <PhoneAppNote surface="app-upload-note" />

              {(upload.files.length > 0 || upload.urlItems.length > 0) && (() => {
                // Cascades the first manually-picked target language across all
                // sibling rows (file + URL) so users only set it once per batch.
                const cascadeTargetLanguage = (originId: string, lang: string) => {
                  if (cascadeFiredRef.current) return;
                  const totalRows = upload.files.length + upload.urlItems.length;
                  const fileSiblings = upload.files.filter((f) => f.id !== originId && !f.targetLanguage && f.mediaCategory !== "text");
                  const urlSiblings = upload.urlItems.filter((u) => u.id !== originId && !u.targetLanguage);
                  if (totalRows <= 1 || fileSiblings.length + urlSiblings.length === 0) return;
                  cascadeFiredRef.current = true;
                  fileSiblings.forEach((f) => upload.updateFile(f.id, { targetLanguage: lang }));
                  urlSiblings.forEach((u) => upload.updateUrlItem(u.id, { targetLanguage: lang }));
                  toastApi.info(
                    t(
                      "TRANSLATE.CASCADE_TOAST",
                      "Set target for all {{count}} files. Click any file to override.",
                      { count: totalRows }
                    )
                  );
                  trackFunnel(funnelName, "cascade_applied", { batch_size: totalRows });
                };
                const clearRowError = (rowId: string) => {
                  if (!perFileTranslateErrors[rowId]) return;
                  setPerFileTranslateErrors((prev) => {
                    const next = { ...prev };
                    delete next[rowId];
                    return next;
                  });
                };
                return (
                  <FileList
                    files={upload.files}
                    urlItems={upload.urlItems}
                    creditItems={creditEstimate?.items}
                    folders={folders}
                    sourceLanguages={sourceLanguageOptions}
                    targetLanguages={targetLanguageOptions}
                    mode={isTranslateMode ? "translate" : "upload"}
                    onLanguageChange={(fileId, lang) => upload.updateFile(fileId, { sourceLanguage: lang })}
                    onTargetLanguageChange={(fileId, lang) => {
                      // Defense-in-depth allowlist; submit-time validateBatch is the authoritative gate.
                      if (!TRANSLATION_LANGUAGES.some((l) => l.transcriptionCode === lang)) return;
                      upload.updateFile(fileId, { targetLanguage: lang });
                      cascadeTargetLanguage(fileId, lang);
                      clearRowError(fileId);
                    }}
                    onFolderChange={(fileId, folderId) => upload.updateFile(fileId, { folderId })}
                    onDubbingChange={(fileId, enabled) => {
                      if (dubbingGateBlocked) return;
                      upload.updateFile(fileId, { enableDubbing: enabled });
                      trackFunnel(funnelName, "dubbing_toggled", {
                        file_index: upload.files.findIndex((f) => f.id === fileId),
                        enabled,
                      });
                    }}
                    onRemoveFile={upload.removeFile}
                    onRetry={upload.retryFile}
                    onUrlLanguageChange={(itemId, lang) => upload.updateUrlItem(itemId, { sourceLanguage: lang })}
                    onUrlTargetLanguageChange={(itemId, lang) => {
                      if (!TRANSLATION_LANGUAGES.some((l) => l.transcriptionCode === lang)) return;
                      upload.updateUrlItem(itemId, { targetLanguage: lang });
                      cascadeTargetLanguage(itemId, lang);
                      clearRowError(itemId);
                    }}
                    onUrlFolderChange={(itemId, folderId) => upload.updateUrlItem(itemId, { folderId })}
                    onUrlDubbingChange={(itemId, enabled) => {
                      if (dubbingGateBlocked) return;
                      upload.updateUrlItem(itemId, { enableDubbing: enabled });
                      trackFunnel(funnelName, "dubbing_toggled", {
                        file_index: upload.files.length + upload.urlItems.findIndex((u) => u.id === itemId),
                        enabled,
                      });
                    }}
                    onRemoveUrlItem={upload.removeUrlItem}
                    disabled={isUploading}
                    perFileErrors={perFileTranslateErrors}
                    dubbingGateBlocked={dubbingGateBlocked}
                  />
                );
              })()}

              {isTranslateMode && (upload.files.length > 0 || upload.urlItems.length > 0) && !creditMode && (
                <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-2">
                  <h3 className="text-sm font-medium">{t("TRANSLATE.COST_ROW_TITLE", "Cost breakdown")}</h3>
                  <div className="flex justify-between text-sm">
                    <span>{t("TRANSLATE.COST_ROW_TRANSLATION", "Translation")}</span>
                    <span className={isFreeCovered ? "text-success font-medium" : ""}>
                      {isFreeCovered
                        ? t("TRANSLATE.COST_ROW_FREE_COVERAGE", "Free with your trial")
                        : `$${translationCost.toFixed(2)}`}
                    </span>
                  </div>
                  {dubbingCost > 0 && (
                    <div className="flex justify-between text-sm">
                      <span>{t("TRANSLATE.COST_ROW_DUBBING", "Dubbing")}</span>
                      <span>${dubbingCost.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>{t("TRANSLATE.COST_ROW_TRANSCRIPTION", "Transcription")}</span>
                    <span>${upload.pricingSummary.displayAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-semibold pt-2 border-t border-border">
                    <span>{t("TRANSLATE.COST_ROW_TOTAL", "Total")}</span>
                    <span className="text-foreground">
                      {effectiveTotalAmount === 0
                        ? t("UPLOAD_FILE.FREE", "Free")
                        : `$${effectiveTotalAmount.toFixed(2)}`}
                    </span>
                  </div>
                </div>
              )}

              {isTranslateMode && (upload.files.length > 0 || upload.urlItems.length > 0) && creditMode && creditEstimate && (
                <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-2" role="status" aria-live="polite" aria-atomic="true">
                  <h3 className="text-sm font-medium">{t("CREDITS.ESTIMATE_TITLE", "Estimated cost")}</h3>
                  <div className="flex justify-between text-sm">
                    <span>{t("TRANSLATE.COST_ROW_TRANSLATION", "Translation")}</span>
                    <span>{formatCredits(creditEstimate.breakdown.translation)}</span>
                  </div>
                  {creditEstimate.breakdown.dubbing > 0 && (
                    <div className="flex justify-between text-sm">
                      <span>{t("TRANSLATE.COST_ROW_DUBBING", "Dubbing")}</span>
                      <span>{formatCredits(creditEstimate.breakdown.dubbing)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>{t("TRANSLATE.COST_ROW_TRANSCRIPTION", "Transcription")}</span>
                    <span>{formatCredits(creditEstimate.breakdown.transcription)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-semibold pt-2 border-t border-border">
                    <span>{t("CREDITS.ESTIMATED_TOTAL", "Estimated total")}</span>
                    <span className="text-foreground">
                      {creditEstimate.approximate ? "≈ " : ""}
                      {formatCredits(creditEstimate.credits)}
                    </span>
                  </div>
                  <p className="text-[11px] text-muted-foreground pt-1">
                    {t("CREDITS.ESTIMATE_NOTE", "Estimate only, you're billed for actual usage.")}
                  </p>
                </div>
              )}

              {hasFiles && (
                <div className="rounded-xl border border-border bg-card p-4 flex flex-col gap-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-muted-foreground">
                        {t("UPLOAD_FILE.FILE_COUNT", { count: totalFiles, defaultValue: "{{count}} file(s)" })}
                        {totalDuration > 0 && (
                          <span className="ml-2">
                            {t("UPLOAD_FILE.TOTAL_DURATION", { duration: formatDuration(totalDuration), defaultValue: "- Total: {{duration}}" })}
                          </span>
                        )}
                      </span>
                      {!creditMode && (
                        <span className={cn("font-medium", effectiveTotalAmount > 0 ? "text-foreground" : "text-success")}>
                          {effectiveTotalAmount > 0 ? (
                            `${upload.pricingSummary.currency} $${effectiveTotalAmount.toFixed(2)}`
                          ) : (
                            t("UPLOAD_FILE.FREE", "Free")
                          )}
                        </span>
                      )}
                      {creditMode && creditEstimate && creditsApplied > 0 && (
                        <span className="font-medium text-foreground">
                          {t("UPLOAD_FILE.CREDIT_NET_SUMMARY", {
                            cost: formatCredits(grossCredits),
                            applied: formatCredits(creditsApplied),
                            net: netDueNow > 0 ? formatCredits(netDueNow) : t("UPLOAD_FILE.FREE", "Free"),
                            defaultValue: "{{cost}} cost, {{applied}} credits applied, you pay {{net}}",
                          })}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                      {upload.pricingSummary.isExpired && (
                        <p className="text-sm text-danger text-center sm:text-right" data-testid="upload-expired-warning">
                          {t("UPLOAD_FILE.PAYMENT_REQUIRED", "Payment required.")}{" "}
                          <button
                            type="button"
                            onClick={() => openSubscriptionGate({ source: "upload_expired" })}
                            className="underline hover:text-foreground transition-colors"
                          >
                            {t("UPLOAD_FILE.RENEW_NOW", "Renew now")}
                          </button>
                        </p>
                      )}

                      <Tooltip
                        content={
                          hasMissingTarget
                            ? t("TRANSLATE.SUBMIT_DISABLED_TARGET", "Select a target language for all files")
                            : undefined
                        }
                      >
                        <span className="inline-flex w-full sm:w-auto">
                          <Button
                            type="button"
                            onClick={() => {
                              if (!upload.pricingSummary.canUpload || isUploading || paymentStepOpen) return;

                              // Per-file translate validation. Errors render inline with
                              // role="alert" so screen readers announce them automatically.
                              if (isTranslateMode) {
                                const errors =
                                  upload.validateBatch?.([...upload.files, ...upload.urlItems]) ?? {};
                                if (Object.keys(errors).length > 0) {
                                  setPerFileTranslateErrors(errors);
                                  const now = Date.now();
                                  if (now - lastTargetToastAtRef.current > 2000) {
                                    lastTargetToastAtRef.current = now;
                                    toastApi.error(
                                      t("TRANSLATE.PLEASE_SELECT_TARGET_TITLE", "Translation language required"),
                                      t("TRANSLATE.PLEASE_SELECT_TARGET", "Please select Translation Language."),
                                    );
                                  }
                                  return;
                                }
                                setPerFileTranslateErrors({});
                              }

                              if (isSubmittingRef.current) return;
                              isSubmittingRef.current = true;

                              trackFunnel(funnelName, "submit_clicked", {
                                file_count: totalFiles,
                                total_duration_seconds: totalDuration,
                                estimated_cost: effectiveTotalAmount,
                                goal: personalization.onboardingGoal ? sanitizeString(personalization.onboardingGoal) : undefined,
                                is_mobile: getIsMobile(),
                              });

                              const total = effectiveTotalAmount;

                              if (total === 0) {
                                handleConfirmUpload().catch((err) => {
                                  isSubmittingRef.current = false;
                                  const message = (err as Error)?.message ?? t("UPLOAD.ERROR.GENERIC", "Something went wrong with the upload. Try again.");
                                  toastApi.error(t("UPLOAD_FILE.UPLOAD_ERROR", "Upload failed"), message);
                                }).finally(() => { isSubmittingRef.current = false; });
                                return;
                              }

                              if (isExactBalanceMatch) {
                                setEntireBalanceDialogOpen(true);
                                isSubmittingRef.current = false;
                                return;
                              }

                              trackBeginCheckout(totalFiles, total, ga4Mode, personalization.onboardingGoal);

                              handleConfirmUpload().catch((err) => {
                                isSubmittingRef.current = false;
                                const message = (err as Error)?.message ?? t("UPLOAD.ERROR.GENERIC", "Something went wrong with the upload. Try again.");
                                toastApi.error(t("UPLOAD_FILE.UPLOAD_ERROR", "Upload failed"), message);
                              }).finally(() => { isSubmittingRef.current = false; });
                            }}
                            ref={submitButtonRef}
                            disabled={
                              !upload.pricingSummary.canUpload ||
                              isUploading ||
                              paymentStepOpen ||
                              hasMissingTarget
                            }
                            aria-disabled={
                              !upload.pricingSummary.canUpload ||
                              isUploading ||
                              paymentStepOpen ||
                              hasMissingTarget
                            }
                            aria-label={
                              hasMissingTarget
                                ? t("TRANSLATE.SUBMIT_DISABLED_TARGET", "Select a target language for all files")
                                : undefined
                            }
                            className="w-full sm:w-auto"
                            data-testid="upload-submit"
                          >
                            {isUploading ? (
                              isBackgroundProcessing ? (
                                <span>{t("UPLOAD_FILE.PROCESSING_IN_BACKGROUND_SHORT", "Processing…")}</span>
                              ) : (
                                <>
                                  <LoaderCircle className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                                  <span>{isTranslateMode ? t("TRANSLATE_FILE.TRANSLATE", "Translate") + "..." : t("UPLOAD_FILE.UPLOADING_FILES", "Uploading…")}</span>
                                </>
                              )
                            ) : creditMode
                              ? (creditEstimate && netDueNow > 0
                                ? t("UPLOAD.SUBMIT_BUTTON", {
                                  amount: `${creditEstimate.approximate ? "≈ " : ""}${formatCredits(netDueNow)}`,
                                  defaultValue: "Upload ({{amount}})",
                                })
                                : t("UPLOAD_FILE.UPLOAD", "Upload"))
                              : isTranslateMode
                              ? (effectiveTotalAmount === 0
                                ? t("TRANSLATE.SUBMIT_FREE", "Translate")
                                : t("TRANSLATE.SUBMIT_BUTTON", {
                                  amount: `$${effectiveTotalAmount.toFixed(2)}`,
                                  defaultValue: "Translate ({{amount}})",
                                }))
                              : effectiveTotalAmount > 0
                                ? t("UPLOAD.SUBMIT_BUTTON", {
                                  amount: `$${effectiveTotalAmount.toFixed(2)}`,
                                  defaultValue: "Upload ({{amount}})",
                                })
                                : t("UPLOAD_FILE.UPLOAD", "Upload")}
                          </Button>
                        </span>
                      </Tooltip>
                    </div>
                  </div>

                </div>
              )}

            </>
          )}

          {/* Inline Step 2: both inPaymentStep (state) and paymentStepOpen (UI) must
          be true — single-flag gating leaves a blank card when they diverge. */}
          {inPaymentStep && paymentStepOpen && personalization.isLoading && (
            <Card className="p-4 max-w-3xl mx-auto w-full" data-testid="upload-payment-step-skeleton">
              <div className="h-20 animate-pulse rounded bg-muted" />
            </Card>
          )}
          {inPaymentStep && paymentStepOpen && !personalization.isLoading && canManageBilling && personalization.paymentSource !== PaymentSourceType.Revenuecat && (
            <Card className="p-4 max-w-3xl mx-auto w-full" data-testid="upload-payment-step" aria-live="polite" aria-atomic="true">
              {personalization.paymentSource === PaymentSourceType.Direct_Deposit ? (
                <>
                  <div className="mb-4">
                    <h2 ref={step2HeaderRef} tabIndex={-1} className="text-lg font-semibold focus-visible:outline-none" data-testid="step2-header">{t("UPLOAD_FILE.STEP_TWO")}</h2>
                    <p className="text-sm text-muted-foreground">
                      Your workspace is on invoice billing. Contact sales to process this upload.
                    </p>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => { setPaymentStepOpen(false); upload.reset(); isSubmittingRef.current = false; setTimeout(() => submitButtonRef.current?.focus(), 0); }}>Go back</Button>
                    <Button type="button" onClick={() => { trackFunnel("booking", "cta_clicked", { surface: "upload_direct_deposit", target: "customer-meeting" }); pushDataLayer("Booking", "CTA Clicked", "upload_direct_deposit", undefined, { surface: "upload_direct_deposit", target: "customer-meeting" }); window.open(withBookingUtms(bookingCalendarUrl("customer-meeting"), "upload_direct_deposit", { userId: user?._id }), "_blank", "noopener,noreferrer"); }}>Contact sales</Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <h2 ref={step2HeaderRef} tabIndex={-1} className="text-lg font-semibold focus-visible:outline-none" data-testid="step2-header">{t("UPLOAD_FILE.STEP_TWO")}</h2>
                      <p className="text-sm text-muted-foreground" data-testid="step2-subhead">{step2Subhead}</p>
                    </div>
                    <div className="sm:text-right">
                      <p className="text-xs text-muted-foreground">{t("UPLOAD_FILE.CONFIRM_TOTAL_COST", "Total Cost")}</p>
                      <p className="text-xl font-semibold">{upload.pricingSummary.currency} ${effectiveTotalAmount.toFixed(2)}</p>
                    </div>
                  </div>

                  {selectedSource?.type === "card" && effectiveTotalAmount > 0 && effectiveTotalAmount < STRIPE_CARD_MINIMUM_USD && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {t("UPLOAD_FILE.CARD_MINIMUM_NOTE", "Speak cannot process transactions less than ${{min}} USD with a card. Please add balance to process these media files.", { min: STRIPE_CARD_MINIMUM_USD.toFixed(2) })}
                    </p>
                  )}

                  {!hasCardOnFile && upload.currentBalance === 0 && personalization.paymentSource === PaymentSourceType.Paddle && paddleSources.length === 0 ? (
                    <div className="mb-4 rounded-md border border-border p-4 text-sm">
                      <p className="text-foreground">No payment methods available.</p>
                      <p className="mt-1 text-xs text-muted-foreground">Your workspace uses Paddle billing. Contact sales to add a payment method.</p>
                    </div>
                  ) : !hasCardOnFile && upload.currentBalance === 0 && personalization.paymentSource !== PaymentSourceType.Paddle ? (
                    <div className="mb-4" data-testid="payment-method-picker">
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Tooltip content={!canManageBilling ? t("PAYMENT.MANAGE_BILLING_DISABLED", "Contact your account admin to manage billing.") : undefined}>
                          <span className="inline-flex w-full">
                            <Button
                              type="button"
                              variant="outline"
                              className="w-full"
                              disabled={!canManageBilling}
                              onClick={() => { upload.enterAddPaymentMethod(); setAddSourceOpen(true); }}
                              data-testid="add-card-cta"
                            >
                              <CreditCard aria-hidden="true" className="mr-2 h-5 w-5" />
                              {t("PAYMENT.ADD_CARD", "Pay by card")}
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip content={!canManageBilling ? t("PAYMENT.MANAGE_BILLING_DISABLED", "Contact your account admin to manage billing.") : undefined}>
                          <span className="inline-flex w-full">
                            <Button
                              type="button"
                              variant="outline"
                              className="w-full"
                              disabled={!canManageBilling || paddleActivating}
                              onClick={() => { upload.enterAddPaymentMethod(); setAddSourceOpen(true); }}
                              data-testid="add-paddle-cta"
                            >
                              <Wallet aria-hidden="true" className="mr-2 h-5 w-5" />
                              {t("PAYMENT.CONNECT_PAYPAL", "Connect PayPal")}
                            </Button>
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                  ) : (
                    <div className="mx-auto w-full max-w-md">
                      <div className="mb-3">
                        <SelectSource
                          onSelect={(source) => {
                            trackFunnel(funnelName, "method_selected", { payment_method: source.type, source: "upload" });
                            setSelectedSource(source);
                            upload.enterChargeConfirm();
                          }}
                          selectedSourceId={selectedSource?.id}
                          showBalance={true}
                          showPaypalConnect={true}
                          canManageBilling={canManageBilling}
                        />
                        {selectedSource?.type === "balance" && upload.currentBalance < effectiveTotalAmount && (() => {
                          const rawShortfall = effectiveTotalAmount - upload.currentBalance;
                          // AddBalance dialog enforces ADD_BALANCE_MIN_USD ($5). When the
                          // raw shortfall is below the minimum, telling users "$0.30 more
                          // needed" creates a UX conflict with the dialog's $5 floor.
                          // Frame as a minimum-floor message in that case.
                          const showsExactShortfall = rawShortfall >= ADD_BALANCE_MIN_USD;
                          const dialogDefault = Math.max(rawShortfall, ADD_BALANCE_MIN_USD);
                          return (
                            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                              <p className="text-xs text-warning">
                                {showsExactShortfall
                                  ? t("UPLOAD.SHORTFALL_EXACT", "${{amount}} more needed to use balance.", { amount: rawShortfall.toFixed(2) })
                                  : t("UPLOAD.SHORTFALL_MIN", "Add balance to continue (top-ups start at ${{min}}).", { min: ADD_BALANCE_MIN_USD })}
                              </p>
                              <button
                                type="button"
                                onClick={() => {
                                  track("payment", "upload_shortfall_topup_clicked", {
                                    shortfall: rawShortfall,
                                    clamped: !showsExactShortfall,
                                  });
                                  setAddBalanceShortfall(dialogDefault);
                                  setAddBalanceContext("step2-card-min");
                                  setAddBalanceOpen(true);
                                }}
                                className="text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                                data-testid="upload-shortfall-topup"
                              >
                                {t("UPLOAD.TOP_UP_NOW", "Top up now →")}
                              </button>
                            </div>
                          );
                        })()}
                      </div>

                      {hasCardOnFile && personalization.paymentSource !== PaymentSourceType.Paddle && canManageBilling && (
                        <button
                          type="button"
                          onClick={() => { upload.enterAddPaymentMethod(); setAddSourceOpen(true); }}
                          className="mb-4 text-xs text-primary hover:underline"
                          data-testid="add-payment-secondary-link"
                        >
                          {t("PAYMENT.ADD_CARD", "Add card")}
                        </button>
                      )}
                    </div>
                  )}

                  {(paddleReconciling || paddleActivating) && (
                    <p
                      role="status"
                      aria-live="polite"
                      className="mb-3 rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground"
                    >
                      {t("PAYMENT.PADDLE_RECONCILING", "We're confirming your PayPal payment — this can take a moment.")}
                    </p>
                  )}

                  {selectedSource && !paddleReconciling && (
                    <p className="mb-3 text-sm text-muted-foreground" role="status">
                      {(() => {
                        const balance = upload.currentBalance ?? 0;
                        const total = effectiveTotalAmount;
                        const shortfallAmount = Math.max(total - balance, 0);
                        if (balance > 0 && total > balance) {
                          return t("UPLOAD.CHARGE_TOPUP", {
                            shortfall: `$${shortfallAmount.toFixed(2)}`,
                            total: `$${total.toFixed(2)}`,
                            defaultValue:
                              "You'll be charged {{shortfall}} to top up your balance to {{total}}. Then we'll resume your upload automatically.",
                          });
                        }
                        return t("UPLOAD.CHARGE_DIRECT", {
                          amount: `$${total.toFixed(2)}`,
                          defaultValue: "Charging {{amount}} to your card.",
                        });
                      })()}
                    </p>
                  )}

                  <div className="flex flex-col sm:flex-row justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isCharging}
                      onClick={() => {
                        trackFunnel(funnelName, "cancel_at_payment", {
                          payment_method_context: selectedSource ? "has_card" : "no_card",
                          estimated_cost: effectiveTotalAmount,
                        });
                        trackUploadAbandon("payment", mode, personalization.onboardingGoal);
                        toastApi.info(t("UPLOAD.CANCELLED_INFO", "Upload cancelled. Any temporary files will be removed automatically."), "");
                        setPaymentStepOpen(false);
                        setSelectedSource(null);
                        upload.reset();
                        isSubmittingRef.current = false;
                        setTimeout(() => submitButtonRef.current?.focus(), 0);
                      }}
                    >
                      {t("ACTION.CANCEL", "Cancel")}
                    </Button>
                    {(hasCardOnFile || upload.currentBalance > 0 || personalization.paymentSource === PaymentSourceType.Paddle || selectedSource !== null) && (
                      // Wrapping <div> is required: a disabled Button has pointer-events:none
                      // so the Tooltip wouldn't fire on hover otherwise.
                      <Tooltip
                        content={
                          !selectedSource
                            ? t("CREATE_SUBSCRIPTION.SELECT_PAYMENT_METHOD", "Select a payment method to continue")
                            : (selectedSource.type === "balance" && upload.currentBalance < effectiveTotalAmount)
                              ? t("UPLOAD_FILE.INSUFFICIENT_BALANCE_HELP", "Insufficient credit")
                              : undefined
                        }
                      >
                        <div className="inline-flex w-full sm:w-auto">
                          <Tooltip content={!canManageBilling ? t("PAYMENT.MANAGE_BILLING_DISABLED", "Contact your account admin to manage billing.") : undefined}>
                            <span className="inline-flex w-full">
                              <Button
                                type="button"
                                onClick={handleStep2Pay}
                                disabled={!selectedSource || paddleActivating || isCharging || (selectedSource.type === "balance" && upload.currentBalance < effectiveTotalAmount) || !canManageBilling}
                                data-testid="upload-step2-pay-button"
                                className="w-full sm:w-auto"
                              >
                                {isCharging
                                  ? <><LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" /><span className="sr-only">{t("UPLOAD.PROCESSING_PAYMENT_SR", "Processing payment…")}</span></>
                                  : isTranslateMode
                                    ? `${t("TRANSLATE.PAY_NOW", "Translate now")} USD $${effectiveTotalAmount.toFixed(2)}`
                                    : `${t("ACTION.PAY_NOW", "Pay Now")} ${upload.pricingSummary.currency} $${effectiveTotalAmount.toFixed(2)}`}
                              </Button>
                            </span>
                          </Tooltip>
                        </div>
                      </Tooltip>
                    )}
                  </div>

                  {/* Direct-deposit is handled in the sibling branch above; TS narrows
                  paymentSource to Stripe | null here so an explicit guard would
                  be unreachable (TS2367). Structural JSX exclusion is the guarantee. */}
                  {personalization.paymentSource !== PaymentSourceType.Paddle && (
                    <p className="mt-3 text-center text-sm text-muted-foreground">
                      <Lock className="mr-1 inline h-3 w-3" aria-hidden="true" />{t("UPLOAD.SECURED_BY_STRIPE", "Secured by Stripe")}
                    </p>
                  )}

                  <AddSource
                    open={addSourceOpen}
                    onClose={() => {
                      setAddSourceOpen(false);
                      // Keep inPaymentStep true so Step 2 stays rendered after the dialog dismisses.
                      upload.enterTopupRequired();
                    }}
                    onSuccess={(addedSource) => {
                      setAddSourceOpen(false);
                      if (!addedSource) return;
                      if (addedSource.type === "card") {
                        const newSource = { id: addedSource.id, type: "card" as const };
                        setSelectedSource(newSource);
                        pendingSourceRef.current = newSource;
                        trackFunnel(funnelName, "auto_pay_after_card_add", { payment_method: "card" });
                        void handleStep2Pay();
                      } else if (addedSource.type === "paddle") {
                        trackFunnel(funnelName, "paypal_added_in_upload", { payment_method: "paddle" });
                        // Wait until the PayPal method is confirmed active before enabling Pay Now.
                        // Charging before the Paddle subscription is active fails server-side with
                        // "URL called is invalid". Mirrors Angular's wait-then-charge sequencing.
                        setPaddleActivating(true);
                        void (async () => {
                          const deadline = Date.now() + 20000;
                          while (Date.now() < deadline) {
                            await new Promise((r) => setTimeout(r, 2000));
                            const { data } = await refetchPaddleSources();
                            if (data?.some((m) => m.id === addedSource.id)) break;
                          }
                          setSelectedSource({ id: addedSource.id, type: "paddle" as const });
                          setPaddleActivating(false);
                        })();
                      }
                    }}
                  />
                </>
              )}
            </Card>
          )}

        </div>{/* end inner content */}
      </div>{/* end card shell */}

      {/* Exact-balance confirmation: cost equals current balance, so the user
          must explicitly consent before we drain the account to $0.00. */}
      <Dialog open={entireBalanceDialogOpen} onClose={() => setEntireBalanceDialogOpen(false)}>
        <DialogHeader>
          <h2 className="text-base font-semibold text-foreground">
            {t("UPLOAD.PAYMENT.ENTIRE_BALANCE_TITLE", "Use your entire credit?")}
          </h2>
        </DialogHeader>
        <DialogBody>
          <p className="text-sm text-muted-foreground">
            {t("UPLOAD.PAYMENT.ENTIRE_BALANCE_BODY", {
              amount: `$${effectiveTotalAmount.toFixed(2)}`,
              defaultValue:
                "This upload costs exactly {{amount}}, which matches your full balance. After uploading, your balance will be $0.00.",
            })}
          </p>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setEntireBalanceDialogOpen(false)}>
            {t("ACTION.CANCEL", "Cancel")}
          </Button>
          <Button
            type="button"
            onClick={() => {
              setEntireBalanceDialogOpen(false);
              isSubmittingRef.current = true;
              trackBeginCheckout(upload.files.length, effectiveTotalAmount, ga4Mode, personalization.onboardingGoal);
              handleConfirmUpload().catch((err) => {
                isSubmittingRef.current = false;
                const message = (err as Error)?.message ?? t("UPLOAD.ERROR.GENERIC", "Something went wrong with the upload. Try again.");
                toastApi.error(t("UPLOAD_FILE.UPLOAD_ERROR", "Upload failed"), message);
              }).finally(() => { isSubmittingRef.current = false; });
            }}
            data-testid="upload-entire-balance-confirm"
          >
            {t("UPLOAD.PAYMENT.ENTIRE_BALANCE_CTA", "Yes, use entire credit")}
          </Button>
        </DialogFooter>
      </Dialog>

      <AddBalance
        open={addBalanceOpen}
        onClose={() => {
          setAddBalanceOpen(false);
          // Restore Step 2 visibility so user isn't left on a blank screen after dismissing.
          if (addBalanceContext === "step2-card-min" || addBalanceContext === "step2-paddle-min") {
            setPaymentStepOpen(true);
          }
          setAddBalanceContext(null);
        }}
        defaultAmount={addBalanceShortfall ?? ADD_BALANCE_DEFAULT_USD}
        canManageBilling={canManageBilling}
        source="upload_payment_step"
        onSuccess={(newBalance) => {
          const ctx = addBalanceContext;
          setAddBalanceOpen(false);
          setAddBalanceContext(null);

          if (creditMode && ctx === "pre-s3-shortfall") {
            resumeUploadAfterPayment();
            return;
          }

          if (newBalance >= effectiveTotalAmount) {
            isSubmittingRef.current = true;
            if (ctx === "pre-s3-shortfall") {
              // Files not yet uploaded — run the full upload + charge path.
              handleConfirmUpload().catch((err) => {
                isSubmittingRef.current = false;
                toastApi.error(
                  t("UPLOAD_FILE.UPLOAD_ERROR", "Upload failed"),
                  err instanceof Error ? err.message : "",
                );
              });
            } else if (ctx === "step2-card-min" || ctx === "step2-paddle-min") {
              // Files already in S3 — debit balance, then process. Mirrors the
              // auto-charge path above: charge BEFORE process, with a distinct
              // toast on charge-fail vs process-fail-after-charge. Without the
              // explicit charge call, /media/process/file marks media
              // isPaid:true without ever debiting balance — silent revenue loss.
              //
              // canManageBilling is checked at Step 2 entry and again at
              // AddBalance dialog open. It is not re-checked here — permission
              // changes mid-dialog are eventual-consistent and rare. The server
              // enforces billing permission independently on
              // /payment/createChargeByBalance.
              void (async () => {
                try {
                  const chargeRes = await createChargeByBalance.mutateAsync(buildBalanceChargePayload(effectiveTotalAmount));
                  if (!chargeRes.success) throw new Error(chargeRes.error ?? "charge_failed");
                  trackSentryRevenue("upload.charged", effectiveTotalAmount, { payment_method: "balance", file_count: totalFiles });
                  trackUploadPurchase(effectiveTotalAmount, totalFiles, "balance", ga4Mode);
                } catch (err) {
                  const msg = err instanceof Error ? err.message : t("PAYMENT.PAYMENT_FAILED", "Payment failed");
                  toastApi.error(t("UPLOAD_FILE.UPLOAD_ERROR", "Upload failed"), msg);
                  trackFailure("payment", "fail", { error_reason: msg, charge_type: "media_upload_balance_topup", payment_method: "balance" });
                  isSubmittingRef.current = false;
                  return;
                }
                try {
                  await upload.chargeAndProcess(upload.processedFiles);
                  queryClient.invalidateQueries({ queryKey: mediaKeys.all });
                  queryClient.invalidateQueries({ queryKey: folderKeys.all });
                  isSubmittingRef.current = false;
                } catch (processErr) {
                  const msg = processErr instanceof Error ? processErr.message : "";
                  toastApi.error(t("UPLOAD_FILE.PAYMENT_OK_PROCESS_FAILED", "Payment succeeded but processing failed"), msg);
                  trackFailure("payment", "process_fail_after_charge", { error_reason: msg, charge_type: "media_upload_balance_topup", payment_method: "balance" });
                  isSubmittingRef.current = false;
                }
              })();
            }
          } else {
            // Balance still insufficient — restore Step 2 so user can choose another method.
            if (ctx === "step2-card-min" || ctx === "step2-paddle-min") {
              setPaymentStepOpen(true);
            }
          }
        }}
      />
    </div>
  );
}

export function UploadPage() {
  const { t } = useTranslation();
  useDocumentTitle(t("UPLOAD.DOCUMENT_TITLE", "Upload"));
  return (
    <Suspense fallback={<PageContentSkeleton variant="form" header={false} count={2} />}>
      <UploadPageContent />
    </Suspense>
  );
}
